/**
 * @author        yontaek@zumper.com (Yon Taek Chung)
 * @copyright     Copyright (c) 2022, Zumper
 * @description   HeaderMobileStr
 */

import { useDisclosure } from '@chakra-ui/hooks'
import { Global } from '@emotion/react'
import { loadGlobalNavLinkpack } from '@zcode-ui/web-zumper-global-state'
import { AutocompleteResult } from '@zcode/bizos'
import { PAGE_NAMES } from '@zcode/mixpanel'
import { useMobileHeaderSticky } from '@zcode/nr-utils'
import { globalNavLinkpack } from '@zcode/nr/es/shared/api'
import { getRouteProps } from '@zcode/nr/es/shared/packages/history-middleware/actions'
import type { Dispatch } from '@zcode/nr/es/shared/types'
import deepclone from '@zcode/nr/es/shared/utils/deepclone'
import type { Filters } from '@zcode/state-type'
import { routes } from '@zcode/zumper-routes'
import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import type { O } from 'ts-toolbelt'
import { MobileNavContext } from '../../MobileNavContext'
import { NAV_MODAL_TYPES, TRACK_USER_MENU_ACTION } from '../../constants'
import { useUserMenuContext } from '../../module/context/UserMenuContext'
import { selectFilters } from '../../module/selectors'
import type { PassedHeaderProps } from '../../types'
import { MobileMenu } from '../MobileMenu'
import { SearchForm } from '../SearchForm'
import { HeaderMobileDetailPage } from './HeaderMobileDetailPage/HeaderMobileDetailPage'
import { HeaderMobileOtherPage } from './HeaderMobileOtherPage/HeaderMobileOtherPage'
import { HeaderMobileSingleInput } from './HeaderMobileSingleInput/HeaderMobileSingleInput'
import * as styles from './style'

export const HeaderMobileStr = ({
  detailCity,
  dispatchMixpanelAutocompleteResultClick,
  dispatchCreateSearch,
  dispatchRemoveSavedSearch,
  getCurrentLocationName,
  dispatchMixpanelNavClick,
  hideMobileAutocomplete,
  handleOnSearchBarClick,
  isCurrentSearchView,
  isDetailView,
  isMessage3,
  locationName,
  mixpanelEvent,
  onLoginClick,
  onLogoutClick,
  onNavLinkClick,
  onSaveSearchModalOpen,
  onSearchButtonClick,
  onSelectAutocompleteResult,
  onSignupClick,
  showMobileAutocomplete,
  dispatchSetNavbarSticky,
  signInHref,
  signUpHref,
  isStatic = false,
  url,
}: PassedHeaderProps) => {
  const { dispatchTrackUserMenuClick } = useUserMenuContext()
  const dispatch = useDispatch<Dispatch>()
  const filters = useSelector(selectFilters)
  const filtersCopy = deepclone(filters)
  const location = isDetailView && detailCity ? detailCity : locationName
  const [showMenu, setShowMenu] = useState(false)
  const [newFilters, setNewFilters] = useState<O.Writable<Filters>>(filtersCopy)
  const [modalType, setModalType] = useState<
    (typeof NAV_MODAL_TYPES)[keyof typeof NAV_MODAL_TYPES]
  >(NAV_MODAL_TYPES.ROOMS)
  const { isOpen, onClose, onOpen } = useDisclosure()
  const store = {
    newFilters,
    setNewFilters,
    modalType,
    setModalType,
    isOpen,
    onClose,
    onOpen,
  }
  const { isSticky } = useMobileHeaderSticky()
  const { pathname } = useLocation()
  const isAddress = pathname.startsWith('/address')

  // Is either new detail page (address, multi unit address, building) or detail page (short term)
  const isAnyDetailPage = (isDetailView || isAddress) && !isCurrentSearchView
  const isOtherPage = !isAnyDetailPage && !isCurrentSearchView

  useEffect(() => {
    dispatchSetNavbarSticky?.(isSticky)
  }, [isSticky])

  useEffect(() => {
    setNewFilters(filters)
  }, [filters])

  const handleAutocompleteResultClick = async (result: AutocompleteResult) => {
    if (result.type === 'building') {
      onSelectAutocompleteResult(result, newFilters)
    } else if (result.type === 'current_position') {
      const { lat, lng } = result
      const city = await getCurrentLocationName({ lat, lng })
      const autocompleteResult = new AutocompleteResult({ ...result._data, city: city?.name })

      onSelectAutocompleteResult(autocompleteResult, newFilters)
    } else {
      onSelectAutocompleteResult(result, newFilters)
    }

    dispatchMixpanelNavClick?.({ section: 'Location', isMobile: true })
    hideMobileAutocomplete?.()
  }

  const applyStickyStyles = isSticky && isCurrentSearchView

  const handleHamburgerClick = useCallback(() => {
    setShowMenu(true)
    dispatch(
      loadGlobalNavLinkpack({
        api: globalNavLinkpack,
        routeProps: dispatch(getRouteProps({ routes })),
      })
    )
    dispatchTrackUserMenuClick(TRACK_USER_MENU_ACTION.MENU_OPEN)
  }, [dispatch])

  const showOnlySummaryBar = applyStickyStyles || !isCurrentSearchView
  const isShort = showOnlySummaryBar && !applyStickyStyles

  const pageName = isCurrentSearchView ? PAGE_NAMES.SEARCH : PAGE_NAMES.DETAIL
  const onSearchBarClick = () => {
    handleOnSearchBarClick?.(pageName)
    onSearchButtonClick?.()
  }

  return (
    <MobileNavContext.Provider value={store}>
      <Global styles={styles.root(isShort)} />
      <div id="mobileHeader" className="z-[1031]">
        {isCurrentSearchView && (
          <HeaderMobileSingleInput
            applyStickyStyles={applyStickyStyles}
            dispatchCreateSearch={dispatchCreateSearch}
            dispatchMixpanelAutocompleteResultClick={dispatchMixpanelAutocompleteResultClick}
            dispatchRemoveSavedSearch={dispatchRemoveSavedSearch}
            handleHamburgerClick={handleHamburgerClick}
            location={location}
            mixpanelEvent={mixpanelEvent}
            onSaveSearchModalOpen={onSaveSearchModalOpen}
            onSearchButtonClick={onSearchBarClick}
            onSelectAutocompleteResult={handleAutocompleteResultClick}
            url={url}
          />
        )}
        {isAnyDetailPage && (
          <HeaderMobileDetailPage
            dispatchCreateSearch={dispatchCreateSearch}
            dispatchRemoveSavedSearch={dispatchRemoveSavedSearch}
            handleHamburgerClick={handleHamburgerClick}
            isDetailView={isDetailView}
            isStatic={isStatic}
            onSaveSearchModalOpen={onSaveSearchModalOpen}
            onSearchButtonClick={onSearchBarClick}
            url={url}
          />
        )}
        {isOtherPage && (
          <HeaderMobileOtherPage
            handleHamburgerClick={handleHamburgerClick}
            onSearchButtonClick={onSearchButtonClick}
          />
        )}
        {showMobileAutocomplete && (
          <SearchForm
            dispatchMixpanelAutocompleteResultClick={dispatchMixpanelAutocompleteResultClick}
            hideMobileAutocomplete={hideMobileAutocomplete}
            isMobile
            onSelectAutocompleteResult={handleAutocompleteResultClick}
            pageName={pageName}
            placeholder="Search by location or property name"
            showMobileAutocomplete={showMobileAutocomplete}
          />
        )}
        <MobileMenu
          isCurrentSearchView={isCurrentSearchView}
          isDetailView={isDetailView}
          isHomepageView={false}
          isMessage3={isMessage3}
          onClose={() => setShowMenu(false)}
          onLoginClick={onLoginClick}
          onLogoutClick={onLogoutClick}
          onNavLinkClick={onNavLinkClick}
          onSignupClick={onSignupClick}
          showModal={showMenu}
          signInHref={signInHref}
          signUpHref={signUpHref}
        />
      </div>
    </MobileNavContext.Provider>
  )
}
